import {
	ReactElement,
	useState
} from "react";
import {
	Anchor,
	Checkbox,
	Dialog,
	FormattedText,
	FormItem,
	FormLabel,
	Toolbar,
	ValidationError
} from "client/Components";
import {
	CultureResolvableMap,
	localize,
	resolveForCurrentCulture
} from "client_legacy/ndaw/culture";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useUnmountedCancellation
} from "client/Hooks";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import { IValidationConfiguration } from "client/Validation/builder";
import { isTrue } from "client/Validation/rules";
import {
	provisionKellyTenant,
	ProvisionKellyTenantModel,
	ProvisionKellyTenantPrerequisite
} from "client/Api/AIAssistantSettings/aIAssistantSettings";
import { HttpRequestError } from "client/Api/httpClient";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { never } from "client_legacy/ndaw/errors";
import { Guid } from "client_legacy/ndaw/guid";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { isNil } from "client/Misc/utils";
import styles from "./ProvisionKellyHelpsDialog.module.less";

export type ProvisionKellyHelpsDialogProps = {
	kellyHelpsUrl: string;
	onProvisioned: (tenantId: Guid) => void;
	onClose: () => void;
};

export const ProvisionKellyHelpsDialog = (props: ProvisionKellyHelpsDialogProps): ReactElement => {
	const {
		kellyHelpsUrl,
		onProvisioned,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [formData, setFormData] = useState<ProvisionKellyTenantModel>(EMPTY_FORM);

	const formHandler: IFormHandler<ProvisionKellyTenantModel> = useFormHandler<ProvisionKellyTenantModel>(
		formData,
		setFormData,
		validationConfiguration
	);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const {
		currentUserIdentity,
		emailAddress
	} = userConfiguration();

	const licenseAgreementUrl: string = resolveForCurrentCulture(LICENSE_AGREEMENT_URLS);
	const trustCenterUrl: string = resolveForCurrentCulture(TRUST_CENTER_URLS);

	const provisionHandler = async (): Promise<void> => {
		if (isBusy) {
			return;
		}

		setIsBusy(true);

		try {
			const validation: InputValidationResult = await formHandler
				.validation
				.validate(globalCancellation);

			if (validation.kind !== InputValidationResultKind.Valid) {
				setIsBusy(false);
				return;
			}

			const tenantId: Guid = await provisionKellyTenant(formData, globalCancellation);

			onProvisioned(tenantId);
		} catch (e) {
			if (isCancelledError(e)) {
				return;
			}

			if (!formHandler.validation.tryProcessServerError(e)) {
				alertManager.showError(
					e instanceof HttpRequestError
						? getRequestErrorMessage(e)
						: localize("_GeneralErrorMessage")
				);
				console.error(e);
			}

			setIsBusy(false);
		}
	};

	return (
		<Dialog
			hasBusyIndicator={isBusy}
			trackingId="provision-kelly-helps-dialog"
		>
			<Dialog.Header title={localize("_SetUpKellyHelps")} />
			<Dialog.Body>
				<p>
					<FormattedText
						text={localize("_KellyHelpsIntroductionInfo")}
						thingsToWrap={[
							{
								blockId: "KellyHelps",
								wrap: x => (
									<Anchor
										href={kellyHelpsUrl}
										target="_blank"
										trackingId="kelly-helps-link"
									>
										{x}
									</Anchor>
								)
							}
						]}
					/>
				</p>
				<p>{localize("_AiAssistantSharedInformationInfo")}</p>
				<div className={styles.details}>
					<SharedDetail
						label={localize("_FirstName")}
						value={currentUserIdentity.firstName}
					/>
					<SharedDetail
						label={localize("_LastName")}
						value={currentUserIdentity.lastName}
					/>
					<SharedDetail
						label={localize("_EmailAddress")}
						value={emailAddress}
					/>
				</div>
				<FormItem>
					<div className={styles.agreement}>
						<Checkbox
							{...formHandler.for("hasAcceptedTerms").attachToCheckbox()}
						/>
						<FormLabel>
							<div className={styles.agreementText}>
								<FormattedText
									text={localize("_LicenseAgreementAndTrustCenterConsent")}
									thingsToWrap={[
										{
											blockId: "LicenseAgreement",
											wrap: x => (
												<Anchor
													href={licenseAgreementUrl}
													target="_blank"
													trackingId="license-agreement-link"
												>
													{x}
												</Anchor>
											)
										},
										{
											blockId: "TrustCenter",
											wrap: x => (
												<Anchor
													href={trustCenterUrl}
													target="_blank"
													trackingId="trust-center-link"
												>
													{x}
												</Anchor>
											)
										}
									]}
								/>
							</div>
						</FormLabel>
					</div>
					<ValidationError {...formHandler.for("hasAcceptedTerms").attachToValidationError()} />
				</FormItem>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Provision")}
						onClick={provisionHandler}
						trackingId="provision"
					/>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
ProvisionKellyHelpsDialog.displayName = "ProvisionKellyHelpsDialog";

export type ProvisionKellyHelpsDialogInitialData = Omit<
	ProvisionKellyHelpsDialogProps,
	"onProvisioned" | "onClose"
>;

export function useProvisionKellyHelpsDialog(): [
	element: ReactElement | null,
	open: (initialData: ProvisionKellyHelpsDialogInitialData) => Promise<TryResult<Guid>>
] {
	return useDataInputDialog<ProvisionKellyHelpsDialogInitialData, Guid>(
		(initialData, onClose, onProvisioned) => (
			<ProvisionKellyHelpsDialog
				{...initialData}
				onProvisioned={onProvisioned}
				onClose={onClose}
			/>
		)
	);
}

type SharedDetailProps = {
	label: string;
	value: string | null;
};

const SharedDetail = (props: SharedDetailProps): ReactElement | null => {
	const {
		label,
		value
	} = props;

	if (isNil(value)) {
		return null;
	}

	return (
		<div className={styles.detail}>
			<h4>
				{label}
			</h4>
			<div className={styles.value}>
				{value}
			</div>
		</div>
	);
};
SharedDetail.displayName = "SharedDetail";

function getRequestErrorMessage(error: HttpRequestError): string {
	if (error.statusCode !== 409) {
		return localize("_GeneralErrorMessage");
	}

	let prerequisite: ProvisionKellyTenantPrerequisite;

	try {
		prerequisite = error.readJson<ProvisionKellyTenantPrerequisite>();
	} catch {
		return localize("_GeneralErrorMessage");
	}

	switch (prerequisite) {
		case ProvisionKellyTenantPrerequisite.PrimaryEmailAddress:
			return localize("_AiAssistantRequiresEmailAddressInfo");
		default:
			return never(prerequisite);
	}
}

const EMPTY_FORM: ProvisionKellyTenantModel = {
	hasAcceptedTerms: false
};

const validationConfiguration: IValidationConfiguration<ProvisionKellyTenantModel> =
	createValidationConfiguration<ProvisionKellyTenantModel>();

validationConfiguration.forScalar("hasAcceptedTerms")
	.add(isTrue());

const LICENSE_AGREEMENT_URLS: CultureResolvableMap<string> = {
	standard: "https://support.workspace365.net/en/articles/388793-end-user-license-agreement-eula",
	nl: "https://support.workspace365.net/nl/articles/388793-eindgebruikersovereenkomst-eula"
};

const TRUST_CENTER_URLS: CultureResolvableMap<string> = {
	standard: "https://support.workspace365.net/en/collections/1309071-trust-center",
	nl: "https://support.workspace365.net/nl/collections/1309071-trust-center"
};
